// components/card-fan-animate.ts
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import type { ImageProps } from '@components/image/image';

// If you want to keep using your km-image later, you can still import it.
// import '@components/image/image';

type Img = Partial<ImageProps> & { src?: string; alt?: string };

@customElement('card-fan-animate')
export class CardFanAnimate extends LitElement {
    protected createRenderRoot(): HTMLElement | DocumentFragment {
        return this; // Kein Shadow DOM
    }
    // If you prefer Light DOM, uncomment:
    // protected createRenderRoot() { return this; }

    /** Raw JSON strings coming from attributes */
    @property({ attribute: 'image-left-props' }) imageLeftPropsRaw:
        | string
        | null = null;
    @property({ attribute: 'image-center-props' }) imageCenterPropsRaw:
        | string
        | null = null;
    @property({ attribute: 'image-right-props' }) imageRightPropsRaw:
        | string
        | null = null;

    @state() private leftLoaded = false;
    @state() private middleLoaded = false;
    @state() private rightLoaded = false;
    @state() private isAnimated = false;

    private observe?: IntersectionObserver;

    firstUpdated(): void {
        this.observe = new IntersectionObserver(
            (entries) => {
                for (const entrie of entries) {
                    if (entrie.isIntersecting) {
                        this.isAnimated = true;
                        this.observe?.unobserve(this);
                        break;
                    }
                }
            },
            { threshold: 1 }
        );
        this.observe.observe(this);
    }

    disconnectedCallback(): void {
        this.observe?.disconnect();
        super.disconnectedCallback();
    }

    protected updated(changed: Map<string, unknown>): void {
        // Reset skeletons when the corresponding props change
        if (changed.has('imageLeftPropsRaw')) this.leftLoaded = false;
        if (changed.has('imageCenterPropsRaw')) this.middleLoaded = false;
        if (changed.has('imageRightPropsRaw')) this.rightLoaded = false;
    }

    private onImgLoad(which: 'left' | 'middle' | 'right') {
        if (which === 'left') this.leftLoaded = true;
        if (which === 'middle') this.middleLoaded = true;
        if (which === 'right') this.rightLoaded = true;
    }

    private parse(json: string | null): Img {
        if (!json) return {};
        try {
            return JSON.parse(json) as Img;
        } catch {
            return {};
        }
    }

    render() {
        const cardsClass = { cards: true, animate: this.isAnimated };

        const left = this.parse(this.imageLeftPropsRaw);
        const middle = this.parse(this.imageCenterPropsRaw);
        const right = this.parse(this.imageRightPropsRaw);

        return html`
            <div class="card-fan card-fan-animation py-100 xl:py-0">
                <div class=${classMap(cardsClass)}>
                    <div
                        class=${classMap({
                            card: true,
                            skeleton: !this.leftLoaded,
                        })}
                    >
                        <img
                            src=${left.src ?? ''}
                            alt=${left.alt ?? 'Left Image'}
                            srcset=${left.srcset ?? ''}
                            sizes=${left.sizes ?? ''}
                            loading=${(left as any).loading ?? 'lazy'}
                            @load=${() => this.onImgLoad('left')}
                            style=${this.leftLoaded ? 'opacity:1' : 'opacity:0'}
                        />
                    </div>

                    <div
                        class=${classMap({
                            card: true,
                            skeleton: !this.middleLoaded,
                        })}
                    >
                        <img
                            src=${middle.src ?? ''}
                            alt=${middle.alt ?? 'Middle Image'}
                            srcset=${middle.srcset ?? ''}
                            sizes=${middle.sizes ?? ''}
                            loading=${(middle as any).loading ?? 'lazy'}
                            @load=${() => this.onImgLoad('middle')}
                            style=${this.middleLoaded
                                ? 'opacity:1'
                                : 'opacity:0'}
                        />
                    </div>

                    <div
                        class=${classMap({
                            card: true,
                            skeleton: !this.rightLoaded,
                        })}
                    >
                        <img
                            src=${right.src ?? ''}
                            alt=${right.alt ?? 'Right Image'}
                            srcset=${right.srcset ?? ''}
                            sizes=${right.sizes ?? ''}
                            loading=${(right as any).loading ?? 'lazy'}
                            @load=${() => this.onImgLoad('right')}
                            style=${this.rightLoaded
                                ? 'opacity:1'
                                : 'opacity:0'}
                        />
                    </div>
                </div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'card-fan-animate': CardFanAnimate;
    }
}
